import { ReactElement } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { removeUnderscoreToCapitalize } from '../helpers/FormattingHelpers';
import { getTemperatureLoggerStatusMessage } from '../helpers/LoggerHelpers';
import { LoggerListItem } from '../types/Logger';
import { FlagChips } from './FlagChips/FlagChips';
import LoggerStatusJsx from './LoggerCheckStatus/LoggerCheckStatus';

interface Props {
    logger: LoggerListItem;
    rowNumber: number;
    scanType: string | undefined;
    hideActions?: boolean;
    isFlagAsDamagedEnabled: boolean;
    isDeleting: boolean;
    onDeleteClick: () => void;
    isLoading: boolean;
    onFlagAsDamagedClick?: () => void;
}

const LoggerCheckTableRow = ({
    logger,
    rowNumber,
    scanType,
    hideActions,
    isFlagAsDamagedEnabled,
    isDeleting,
    onDeleteClick,
    isLoading,
    onFlagAsDamagedClick
}: Props): ReactElement => (
    <div role="row" className="row" id={logger.errorOnSubmit || logger.anotherCustomer ? 'error-row' : ''}>
        <div className="col-0">
            {logger.masterStatus === 'pending' || isLoading ? (
                <FontAwesomeIcon icon="spinner" spin={true} size="lg" id="dimgray" title="Loading" />
            ) : (
                rowNumber
            )}
        </div>

        <div className="col-1">
            <div data-testid="logger-id-cell">
                <strong>{logger.loggerId}</strong>
            </div>
            <div className="customer">{logger.customer ? logger.customer.name : 'N/A'}</div>
        </div>

        {scanType !== 'MANUFACTURER-BOX' && (
            <div className="col-2">
                {logger.masterStatus !== 'pending' && !isLoading && (
                    <LoggerStatusJsx
                        status={logger.masterStatus}
                        message={removeUnderscoreToCapitalize(logger.masterStatus)}
                    />
                )}
            </div>
        )}

        {scanType !== 'MANUFACTURER-BOX' && (
            <div className={`col-3 ${scanType}`}>
                {logger.status && !isLoading && (
                    <LoggerStatusJsx
                        status={logger.status.calibration?.status}
                        message={logger.status.calibration?.message}
                    />
                )}
            </div>
        )}

        {scanType !== 'MANUFACTURER-BOX' && (
            <div className={`col-4 ${scanType}`}>
                {logger.status && !isLoading && (
                    <LoggerStatusJsx
                        status={logger.status.loggerTest?.status}
                        message={logger.status.loggerTest?.message}
                    />
                )}
            </div>
        )}

        <div className={`col-5 ${scanType}`}>
            {logger.status && !isLoading && (
                <FlagChips
                    status={logger.status.flags?.status}
                    flags={logger.status.flags?.details?.flags ?? []}
                    message={removeUnderscoreToCapitalize(logger.status.flags?.message)}
                />
            )}
        </div>

        <div className={`col-6 ${scanType}`}>
            {logger.status && !isLoading && (
                <LoggerStatusJsx
                    status={logger.status.temperature?.status}
                    message={
                        logger.status?.temperature ? getTemperatureLoggerStatusMessage(logger.status?.temperature) : ''
                    }
                />
            )}
        </div>

        <div className="col-7">
            {logger.status && !isLoading && (
                <LoggerStatusJsx status={logger.status.battery?.status} message={logger.status.battery?.message} />
            )}
        </div>

        <div className={`col-8 ${scanType}`}>
            {logger.status && !isLoading && (
                <LoggerStatusJsx status={logger.status.firmware?.status} message={logger.status.firmware?.message} />
            )}
        </div>

        {scanType !== 'MANUFACTURER-BOX' && (
            <div className={`col-9 ${scanType}`}>
                {logger.status && !isLoading && (
                    <LoggerStatusJsx status={logger.status.sensors?.status} message={logger.status.sensors?.message} />
                )}
            </div>
        )}

        {scanType !== 'MANUFACTURER-BOX' && (
            <div className={`col-10 ${scanType}`}>
                {logger.status && !isLoading && (
                    <LoggerStatusJsx
                        status={logger.status.shipment?.status}
                        message={logger.status.shipment?.message}
                    />
                )}
            </div>
        )}

        {!hideActions && (
            <div className="col-11">
                {isFlagAsDamagedEnabled && onFlagAsDamagedClick && (
                    <button
                        data-testid="flag-as-damaged-button"
                        title="Flag as damaged"
                        disabled={logger.masterStatus === 'pending'}
                        className="action-icon"
                        onClick={onFlagAsDamagedClick}
                    >
                        {!isLoading && <FontAwesomeIcon icon="link-horizontal-slash" size="lg" />}
                    </button>
                )}

                <button
                    title="Remove item"
                    data-testid="remove-item-button"
                    className="action-icon"
                    disabled={isDeleting}
                    onClick={onDeleteClick}
                >
                    {isDeleting ? (
                        <FontAwesomeIcon icon="spinner" spin={true} size="lg" />
                    ) : (
                        <FontAwesomeIcon icon="trash" size="lg" />
                    )}
                </button>
            </div>
        )}
    </div>
);

export default LoggerCheckTableRow;
